import classNames from "classnames";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { StringInput } from "@krea/customer-features/design-system/Input";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import ResponsiveDialog from "@krea/customer-features/shared-components/responsive-dialog";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";
import { COUNTRY_CODE } from "@krea/customer-features/utils";

import { GuarantorFormSchema } from "./formHelpers";

interface IGuarantorsFormModalProps {
  fieldRequestID: number;
  guarantorIndex: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  action: "add" | "edit";
  guarantorName: string;
  onToggleGuarantor: (toggleValue: boolean) => void;
  manuallyAddGuarantor: boolean;
}

export const GuarantorsFormModal = ({
  fieldRequestID,
  guarantorIndex,
  isOpen,
  setIsOpen,
  action,
  guarantorName,
  onToggleGuarantor,
  manuallyAddGuarantor,
}: IGuarantorsFormModalProps) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const isAdviceState = data?.state === LoanApplicationStates.ADVICE;

  const {
    mutate: postFieldRequest,
    isLoading,
    error,
    reset,
  } = usePostFieldRequest(fieldRequestID);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid },
  } = useFormContext<GuarantorFormSchema>();

  const isFinland = settings.countryCode === COUNTRY_CODE.FI;

  const initialValues = getValues(`guarantors.${guarantorIndex}`);
  const initialEmail = getValues(`guarantors.${guarantorIndex}.email`);
  const initialSsn = getValues(`guarantors.${guarantorIndex}.ssn`);

  const onSubmit = useCallback(() => {
    handleSubmit(
      (data) => {
        postFieldRequest(data, {
          onSuccess: () => {
            setIsOpen(false);
            reset();
          },
        });
      },
      (ers) => {
        console.error(ers);
      },
    )();
  }, [handleSubmit, postFieldRequest, reset, setIsOpen]);

  const handleOnClose = useCallback(() => {
    setIsOpen(false);

    if (action === "add") {
      onToggleGuarantor(false);
    }

    setValue(`guarantors.${guarantorIndex}`, {
      ...initialValues,
      email: initialEmail,
      ssn: initialSsn,
    });
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, guarantorIndex, onToggleGuarantor, reset, setIsOpen, setValue]);

  const errors = error?.data?.errors || [];

  return (
    <ResponsiveDialog
      open={isOpen}
      onClose={handleOnClose}
      title={
        action === "add"
          ? t("iaq.guarantor.addContactDetails")
          : t("iaq.guarantor.editContactDetails")
      }
      setOpen={setIsOpen}
    >
      {guarantorName && <p className="tw-font-bold">{guarantorName}</p>}

      {(manuallyAddGuarantor || isFinland) && (
        <StringInput
          className="tw-mb-4"
          control={control}
          name={`guarantors.${guarantorIndex}.ssn`}
          label={t("iaq.guarantor.personalNumberLabel") as string}
          placeholder={t("iaq.guarantor.personalNumberPlaceholder") as string}
        />
      )}

      <StringInput
        className="tw-mb-4"
        control={control}
        name={`guarantors.${guarantorIndex}.email`}
        label={t("iaq.guarantor.emailLabel") as string}
        placeholder={t("iaq.guarantor.emailPlaceholder") as string}
      />

      <div>
        {errors?.map((error: any, index: number) => (
          <Error
            className={classNames({
              "tw-mt-1 tw-ml-7": isAdviceState,
              "tw-mt-1 tw-ml-1": !isAdviceState,
            })}
            key={index + "error"}
          >
            {error.message}
          </Error>
        ))}
      </div>

      <Button
        onClick={onSubmit}
        className="tw-w-full tw-mt-4"
        disabled={isLoading || !isValid}
      >
        {t("commons.save")}
      </Button>
    </ResponsiveDialog>
  );
};
